import { useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Printer, BadgeCheck, TrendingUp, Globe2, Leaf } from "lucide-react";

const fmtRp = (n) => `Rp ${n.toLocaleString("id-ID")}`;

export default function ImpactReportModal({ open, onOpenChange, stats }) {
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("bn:impact-report", { detail: open }));
    return () => window.dispatchEvent(new CustomEvent("bn:impact-report", { detail: false }));
  }, [open]);

  if (!stats) return null;
  const resolution = Math.round((stats.issue_resolution.resolved / stats.issue_resolution.reported) * 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="impact-report-modal" className="max-w-2xl rounded-2xl p-0 overflow-hidden gap-0 max-h-[92vh] flex flex-col">
        <DialogTitle className="sr-only">Executive Impact Report — CSR & Community Readiness</DialogTitle>
        <DialogDescription className="sr-only">Laporan dampak eksekutif siap presentasi untuk sponsor CSR dan mitra ESG, dapat dicetak sebagai PDF.</DialogDescription>

        <div id="executive-impact-report" className="report-print bg-white overflow-y-auto">
          <div className="bg-slate-900 px-8 py-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="bg-emerald-600 text-white rounded-lg p-1"><ShieldCheck className="w-4 h-4" /></span>
                <span className="font-heading font-black text-white">Bergerak<span className="text-emerald-400">Nyata</span></span>
              </div>
              <h2 className="font-heading font-black text-xl text-white mt-2">Executive Impact Report</h2>
              <p className="text-xs text-emerald-300 font-bold uppercase tracking-wider">CSR & Community Readiness · Semester I — 2026</p>
            </div>
            <span className="hidden sm:inline-flex text-[10px] font-bold px-3 py-1.5 rounded-full bg-white/10 text-slate-300 border border-white/20">TERVERIFIKASI PLATFORM</span>
          </div>

          <div className="p-8 space-y-7">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "Relawan Dimobilisasi", val: stats.total_volunteers.toLocaleString("id-ID") },
                { label: "Jam Kontribusi", val: stats.total_hours.toLocaleString("id-ID") },
                { label: "Proyek Terverifikasi", val: stats.completed_projects },
                { label: "Pertumbuhan Jun/Jan", val: "+165%" },
              ].map((s) => (
                <div key={s.label} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                  <p className="font-heading font-black text-2xl text-slate-900">{s.val}</p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            <div data-testid="economic-value-section" className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6">
              <p className="text-xs font-black text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4" /> Total Nilai Ekonomi Relawan
              </p>
              <p className="font-heading font-black text-3xl sm:text-4xl text-emerald-800 mt-2">{fmtRp(stats.economic_value)}</p>
              <p className="text-sm text-emerald-700 mt-2">
                {stats.total_hours.toLocaleString("id-ID")} jam kerja sosial × {fmtRp(stats.hourly_value)}/jam (standar valuasi jam relawan)
                = efisiensi operasional setara <b>{fmtRp(stats.economic_value)}</b> bagi program sosial mitra.
              </p>
            </div>

            <div data-testid="sdg-section">
              <h3 className="font-heading font-extrabold text-slate-900 flex items-center gap-2 mb-4">
                <Globe2 className="w-5 h-5 text-emerald-600" /> Kontribusi Target SDGs
              </h3>
              <div className="space-y-3">
                {stats.sdgs.map((s) => (
                  <div key={s.code} className="flex items-center gap-4">
                    <span className="w-16 shrink-0 text-center text-[10px] font-black px-2 py-1.5 rounded-lg bg-slate-900 text-white">{s.code}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs font-bold text-slate-700 mb-1">
                        <span>{s.name}</span><span>{s.pct}%</span>
                      </div>
                      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${s.pct}%` }} />
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">{s.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div data-testid="region-section" className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                <h4 className="font-heading font-extrabold text-sm text-slate-900 mb-3">Distribusi Wilayah</h4>
                {stats.regions.map((r) => (
                  <div key={r.city} className="flex items-center gap-3 mb-2">
                    <span className="w-20 text-xs font-bold text-slate-600">{r.city}</span>
                    <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-600 rounded-full" style={{ width: `${r.pct}%` }} />
                    </div>
                    <span className="text-xs font-black text-slate-700 w-9 text-right">{r.pct}%</span>
                  </div>
                ))}
              </div>
              <div data-testid="resolution-section" className="bg-slate-900 rounded-2xl p-5 text-center flex flex-col justify-center">
                <p className="text-xs font-black text-emerald-400 uppercase tracking-wider flex items-center justify-center gap-1.5">
                  <Leaf className="w-4 h-4" /> Rasio Penyelesaian Isu Lingkungan
                </p>
                <p className="font-heading font-black text-5xl text-white mt-2">{resolution}%</p>
                <p className="text-xs text-slate-400 mt-2">{stats.issue_resolution.resolved} dari {stats.issue_resolution.reported} isu lingkungan yang dilaporkan warga tuntas ditindaklanjuti</p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400 pt-4 border-t border-slate-100">
              <BadgeCheck className="w-4 h-4 text-emerald-600" />
              Data terverifikasi sistem check-in multi-metode BergerakNyata · Siap audit untuk pelaporan CSR & ESG mitra
            </div>
          </div>
        </div>

        <div className="bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-2 shrink-0">
          <Button data-testid="report-close-btn" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full font-bold">Tutup</Button>
          <Button data-testid="report-print-btn" onClick={() => window.print()}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            <Printer className="w-4 h-4 mr-2" /> Unduh / Cetak Laporan PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
